package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToInt
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Caption
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.ElementScope
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.w3c.dom.Element

/**
 * One point in a series.
 *
 * [label] is what a person reads — a date, a language, a provider. [value] is what is plotted.
 */
data class ChartPoint(val label: String, val value: Double)

/** A named series. Colour is assigned by position, never cycled and never by rank. */
data class ChartSeries(val name: String, val points: List<ChartPoint>)

/**
 * Hand-built SVG charts.
 *
 * **The product had no way to plot anything.** `LProgressBar` and `LProgressRing` were the only
 * quantitative components, so every question shaped like "is this getting better, and how fast" had
 * no answer anywhere in the app — including the one a release manager most needs, which is when a
 * language will be finished at the current rate.
 *
 * These follow fixed mark specs so that every chart in the product looks like it was drawn by the
 * same hand: 2px lines with round caps, markers of at least 8px carrying a 2px surface ring so they
 * stay legible where they cross, bars capped at 24px with a 4px rounded data-end and a square foot
 * on the baseline, area fills at 10% opacity, and hairline recessive gridlines. Text never wears
 * the series colour — a swatch beside it carries identity — because a light hue is illegible as
 * type on either surface.
 */

// --- the SVG plumbing Compose HTML does not ship -----------------------------------------------

@Composable
internal fun Svg(attrs: AttrsScope<Element>.() -> Unit, content: @Composable ElementScope<Element>.() -> Unit) {
    TagElement<Element>(
        elementBuilder = { document.createElementNS("http://www.w3.org/2000/svg", "svg") },
        applyAttrs = attrs,
        content = content,
    )
}

@Composable
internal fun SvgTag(
    name: String,
    attrs: AttrsScope<Element>.() -> Unit,
    content: (@Composable ElementScope<Element>.() -> Unit)? = null,
) {
    TagElement<Element>(
        elementBuilder = { document.createElementNS("http://www.w3.org/2000/svg", name) },
        applyAttrs = attrs,
        content = content,
    )
}

private val document get() = kotlinx.browser.document

// --- sparkline ----------------------------------------------------------------------------------

/**
 * A trend, inline, with no axes.
 *
 * Sits beside a number to say which way it is going. Deliberately has no tooltip: it carries shape,
 * not values — the number beside it is the value.
 */
@Composable
fun LSparkline(
    points: List<Double>,
    modifier: Modifier = Modifier,
    width: Int = 96,
    height: Int = 28,
    color: com.varabyte.kobweb.compose.ui.graphics.Color? = null,
    label: String = t("ds.ds.lchart.trend", "Trend"),
) {
    if (points.size < 2) return
    val palette = ColorMode.current.toSitePalette()
    val stroke = color ?: palette.accent
    val lowest = points.min()
    val highest = points.max()
    val span = (highest - lowest).takeIf { it > 0.0 } ?: 1.0
    val step = (width - 4.0) / (points.size - 1)
    val path = points.mapIndexed { index, value ->
        val x = 2.0 + index * step
        val y = height - 2.0 - ((value - lowest) / span) * (height - 4.0)
        "${if (index == 0) "M" else "L"}${x.round()} ${y.round()}"
    }.joinToString(" ")

    Div(modifier.toAttrs()) {
        Svg({
            attr("viewBox", "0 0 $width $height")
            attr("width", width.toString())
            attr("height", height.toString())
            attr("role", "img")
            attr("aria-label", label)
            attr("focusable", "false")
        }) {
            SvgTag("path", {
                attr("d", path)
                attr("fill", "none")
                attr("stroke", stroke.toString())
                attr("stroke-width", ChartPalette.LINE_WIDTH.toString())
                attr("stroke-linecap", "round")
                attr("stroke-linejoin", "round")
            })
            // The end point, so the eye lands on "now" rather than wandering the whole line.
            SvgTag("circle", {
                attr("cx", (2.0 + (points.size - 1) * step).round())
                attr("cy", (height - 2.0 - ((points.last() - lowest) / span) * (height - 4.0)).round())
                attr("r", "3")
                attr("fill", stroke.toString())
                attr("stroke", palette.surface.toString())
                attr("stroke-width", "2")
            })
        }
    }
}

// --- bars ---------------------------------------------------------------------------------------

/**
 * Faster than the default 60ms, because a chart is one object rather than a list of cards.
 *
 * At 60ms a ten-bar chart takes 600ms to finish assembling, which is long enough to read as the
 * page still loading. 35ms keeps the top-down order legible and has the whole chart settled inside
 * the time a reader spends moving their eye to it.
 */
private const val CHART_ROW_STEP_MS = 35

/** A magnitude comparison across a handful of named things — languages, providers, people. */
@Composable
fun LBarChart(
    points: List<ChartPoint>,
    modifier: Modifier = Modifier,
    valueFormat: (Double) -> String = { formatCount(it.roundToInt()) },
    emptyMessage: String = t("ds.ds.lchart.nothing_to_compare_yet", "Nothing to compare yet."),
    color: com.varabyte.kobweb.compose.ui.graphics.Color? = null,
    /** Names what the table beneath the bars counts. See `LLineChart`'s `caption`. */
    caption: String = t("ds.ds.lchart.comparison", "Comparison"),
) {
    val palette = ColorMode.current.toSitePalette()
    if (points.isEmpty()) {
        ChartEmpty(emptyMessage)
        return
    }
    val highest = max(1.0, points.maxOf { it.value })
    // The painted rows are a picture of a table, so they are hidden from assistive technology and
    // `ChartTable` below is the one announcement. Reading both would say every figure twice — the
    // split `LStatRow` already draws for the same reason.
    Column(modifier.fillMaxWidth().gap(Space.XS).attr("aria-hidden", "true")) {
        points.forEachIndexed { index, point ->
            // **A staggered arrival, and still no animated width.**
            //
            // The bars keep their final width on first paint — `DESIGN.md` is explicit that width,
            // height and layout position never animate, and tweening a layout property on every
            // re-render is what that rule exists to stop. This is the shared row entrance instead:
            // opacity and a transform, both compositor properties, run once as the rows arrive.
            // A ranked comparison that assembles top-down is read in the order it ranks.
            Row(
                Modifier.fillMaxWidth().gap(Space.SM).enterStaggered(index, stepMs = CHART_ROW_STEP_MS),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Span(
                    Modifier
                        .fontSize(TypeScale.Small)
                        .color(palette.textSecondary)
                        .styleModifier {
                            property("min-width", "112px")
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                        .toAttrs(),
                ) {
                    Text(point.label)
                }
                Div(
                    Modifier.styleModifier { property("flex", "1"); property("min-width", "0") }.toAttrs(),
                ) {
                    Div(
                        Modifier.styleModifier {
                            property("height", "${min(ChartPalette.MAX_BAR_THICKNESS, 14.0)}px")
                            property("width", "${(point.value / highest * 100).round()}%")
                            property("background", (color ?: palette.accent).toString())
                            // Square at the baseline, rounded at the data end.
                            property("border-radius", "0 4px 4px 0")
                            property("min-width", "2px")
                            // No `transition: width`. `DESIGN.md` puts it plainly — "Never animate
                            // width, height or layout position" — and `width` is in neither
                            // `Motion.ColorProperties` nor `Motion.SurfaceProperties`, so this was
                            // off-contract as well as off-spec: every re-render tweened a layout
                            // property on the main thread. Bars are laid out at final width on
                            // first paint.
                        }.toAttrs(),
                    ) {}
                }
                Span(
                    Modifier
                        .fontSize(TypeScale.Small)
                        .fontWeight(600)
                        .styleModifier {
                            property("font-variant-numeric", "tabular-nums")
                            property("min-width", "56px")
                            property("text-align", "end")
                        }
                        .toAttrs(),
                ) {
                    Text(valueFormat(point.value))
                }
            }
        }
    }
    ChartTable(
        caption = caption,
        rowHeader = t("ds.ds.lchart.item", "Item"),
        columns = listOf(t("ds.ds.lchart.value", "Value")),
        rows = points.map { it.label to listOf(valueFormat(it.value)) },
    )
}

// --- composition --------------------------------------------------------------------------------

/**
 * How a whole divides — the states of one locale, the origins of one project's translations.
 *
 * A stacked bar rather than a donut: it compares across rows, which is the question people actually
 * ask ("is German further along than French?"), and it survives being 8px tall in a table.
 */
@Composable
fun LSegmentBar(
    segments: List<Triple<String, Double, com.varabyte.kobweb.compose.ui.graphics.Color>>,
    modifier: Modifier = Modifier,
    height: Int = 10,
    /**
     * Names the composition — "German progress", "Credits used this month".
     *
     * It is the table's `<caption>`, verbatim. It used to be a prefix onto which each segment's
     * *percentage* was appended, so a caller who passed a complete sentence got that sentence with
     * ", Used 95%, Left 5%" glued on the end and no way to decline it. The figures now live in the
     * table, where they are the real numbers rather than rounded percentages.
     */
    ariaLabel: String = t("ds.ds.lchart.composition", "Composition"),
    valueFormat: (Double) -> String = { formatCount(it.roundToInt()) },
) {
    val palette = ColorMode.current.toSitePalette()
    val total = segments.sumOf { it.second }.takeIf { it > 0 } ?: return
    Div(
        modifier
            .fillMaxWidth()
            .styleModifier {
                property("display", "flex")
                // Surface-coloured separation: never a stroke drawn around a segment.
                property("gap", "${ChartPalette.SURFACE_GAP}px")
                property("height", "${height}px")
                property("border-radius", "999px")
                property("overflow", "hidden")
                property("background", palette.surfaceSunken.toString())
            }
            // The painted bar is the picture; `ChartTable` below is the reading of it. Same split
            // `LStatRow` makes, and hard rule 7's requirement that a chart ship its numbers as a
            // real table.
            .toAttrs { attr("aria-hidden", "true") },
    ) {
        segments.filter { it.second > 0 }.forEach { (_, value, color) ->
            Div(
                Modifier.styleModifier {
                    property("width", "${(value / total * 100).round()}%")
                    property("background", color.toString())
                    // **No `title` and no `transition: width`.** The title hover-revealed the only
                    // grouped figure on the usage routes, whose spec says "No `title` attribute.
                    // Every number on this page is either rendered or absent; nothing is
                    // hover-revealed" — and it was unreachable by keyboard or touch anyway. The
                    // width transition tweened a layout property, which `DESIGN.md` forbids
                    // outright, on a page whose spec says nothing on it animates.
                }.toAttrs(),
            ) {}
        }
    }
    ChartTable(
        caption = ariaLabel,
        rowHeader = t("ds.ds.lchart.part", "Part"),
        columns = listOf(t("ds.ds.lchart.value", "Value")),
        rows = segments.map { (name, value, _) -> name to listOf(valueFormat(value)) },
    )
}

/**
 * The numbers behind a drawing, as a real `<table>`.
 *
 * House rule 7: "Every chart also renders its numbers as a real table." Nothing in this file used
 * to — the charts carried an `aria-label` summarising the last point of each series, which is a
 * sentence about a chart rather than access to it. `LStatRow` had already paid for this pattern and
 * recorded why ("The painted stack is a picture of a table, which hard rule 7 says owes a real
 * one"); this is the same answer, hoisted to where a chart's table belongs so it is not patched in
 * per page.
 *
 * Visually hidden rather than rendered, because the drawing above it *is* the rendering for a
 * sighted reader and printing both would say everything twice. The drawings are `aria-hidden` in
 * exchange, so this is the single accessible representation rather than a second announcement of
 * the same figures. Where a spec asks for a table that is also *visible* — the credits meter — the
 * page renders `LStatRow` beside the bar; that is a page's decision, not this component's.
 */
@Composable
internal fun ChartTable(
    caption: String,
    rowHeader: String,
    columns: List<String>,
    rows: List<Pair<String, List<String>>>,
) {
    if (rows.isEmpty()) return
    Table(LVisuallyHiddenStyle.toModifier().toAttrs()) {
        Caption { Text(caption) }
        Thead {
            Tr {
                Th(Modifier.toAttrs { attr("scope", "col") }) { Text(rowHeader) }
                columns.forEach { column ->
                    Th(Modifier.toAttrs { attr("scope", "col") }) { Text(column) }
                }
            }
        }
        Tbody {
            rows.forEach { (label, values) ->
                Tr {
                    Th(Modifier.toAttrs { attr("scope", "row") }) { Text(label) }
                    values.forEach { value -> Td { Text(value) } }
                }
            }
        }
    }
}

// --- shared pieces ------------------------------------------------------------------------------

@Composable
internal fun ChartEmpty(message: String) {
    Span(
        Modifier
            .fillMaxWidth()
            .fontSize(TypeScale.Small)
            .color(ColorMode.current.toSitePalette().textSecondary)
            .toAttrs(),
    ) {
        Text(message)
    }
}

internal fun Double.round(): String = ((this * 10).roundToInt() / 10.0).toString()
